import React, { createContext, useContext, useState, useEffect } from 'react';
import en from './locales/en.json';
import es from './locales/es.json';

const translations = { en, es };

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first, then browser language, default to 'en'
    const saved = localStorage.getItem('rtg-language');
    if (saved && translations[saved]) return saved;
    
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) return browserLang;
    
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('rtg-language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Translation function with nested key support (e.g., 'nav.home')
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        // Fallback to English if key not found
        value = translations['en'];
        for (const k2 of keys) {
          value = value?.[k2];
          if (value === undefined) break;
        }
        break;
      }
    }
    
    if (typeof value !== 'string') {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    
    // Replace parameters like {{name}} with actual values
    return value.replace(/\{\{(\w+)\}\}/g, (_, param) => params[param] || `{{${param}}}`);
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  const availableLanguages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  return (
    <LanguageContext.Provider value={{ 
      language, 
      t, 
      changeLanguage, 
      availableLanguages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
